import { useState, useRef, useEffect } from 'react';
import {
  eToNumber,
  formatNumberWihtNoQuote,
} from '@bitbot/shared/utils/quote-value';
import { trpc } from '@bitbot/api';
import { createGlobalStore } from 'hox';
import { useLocalStorageState } from 'ahooks';
import { useMainButton } from '@bitbot/shared/bridge'

export const useSlippage = () => {
  const slippages = [0.1, 0.5, 1, 5];
  const [selectedSlippage, setSelectedSlippage] = useState(0.5);
  const [inputedSlippage, setInputedSlippage] = useState('');
  const slippage = +inputedSlippage ? inputedSlippage : selectedSlippage;
  const [showSlippageDialog, setShowSlippageDialog] = useState(false);
  return {
    slippages,
    selectedSlippage,
    setSelectedSlippage,
    inputedSlippage,
    setInputedSlippage,
    slippage,
    showSlippageDialog,
    setShowSlippageDialog,
  };
};

export const useRateReverse = (
  baseToken: string | null | undefined,
  quoteToken: string | null | undefined,
  rate: number,
) => {
  const [isReverse, setIsReverse] = useState(false);
  return {
    setIsReverse,
    prefixToken: (isReverse ? quoteToken : baseToken) ?? '',
    suffixToken: (isReverse ? baseToken : quoteToken) ?? '',
    isReverse,
    realRate: isReverse
      ? formatNumberWihtNoQuote(1 / rate)
      : formatNumberWihtNoQuote(rate),
  };
};

export const [useSearchDialog] = createGlobalStore(() => {
  const [showSearch, setShowSearch] = useState(false);
  const showRef = useRef<boolean>(false);
  useEffect(() => {
    showRef.current = showSearch;
  }, [showSearch]);
  return {
    showSearch,
    setShowSearch,
    showRef,
  };
});

export const [useOrderConfimDialog] = createGlobalStore(() => {
  const [showOrderConfirm, setShowOrderConfirm] = useState(false);
  const showOrdreConfirmRef = useRef<boolean>(false);
  useEffect(() => {
    showOrdreConfirmRef.current = showOrderConfirm;
  }, [showOrderConfirm]);
  return {
    showOrderConfirm,
    setShowOrderConfirm,
    showOrdreConfirmRef,
  };
});

export const [useTransaction] = createGlobalStore(() => {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabs = ['Open Orders', 'Order History', 'Trade History'];
  return {
    activeIndex,
    setActiveIndex,
    tabs,
  };
});

export const useAuthentication = () => {
  const [authCode, setAuthCode] = useState('');
  const [active, setActive] = useState(false);
  const [otpCodeError, setOtpCodeError] = useState(false);

  useEffect(() => {
    setOtpCodeError(false);
  }, [authCode]);
  return {
    authCode,
    setAuthCode,
    active,
    setActive,
    otpCodeError,
    setOtpCodeError,
  };
};

/**
 * 存储一些全局的状态
 */
export const [useGlobalValueStore] = createGlobalStore(() => {
  const mainButton = useMainButton();
  const homeScrollRef = useRef<HTMLDivElement>(null); //主页containerRef
  const [showBalance, setShowBalance] = useLocalStorageState<boolean>( //控制余额的显示与隐藏
    'isShowBalance',
    {
      defaultValue: true,
    },
  );
  const [isChangingWallet, setIsChangingWallet] = useState(false);
  const [currentWalletAddress, setCurrentWalletAddress] =
    useLocalStorageState<string>('current-wallet-address');
  const [recentWallets, setRecentWallets] = useLocalStorageState<
    //存储最近send 的钱包地址
    {
      address: string;
      time: number;
      chain: 'base';
    }[]
  >('recent-wallets', {
    defaultValue: [],
  });
  const [showInputWalletName, setShowInputWalletName] = useState(false);
  const showInputWalletNameRef = useRef<boolean>(false);
  useEffect(() => {
    showInputWalletNameRef.current = showInputWalletName;
  }, [showInputWalletName]);
  const _setShowInputWalletName = (value: boolean) => {
    if (!value) {
      mainButton?.hide();
      // setShowInputWalletName(value);
      setTimeout(() => {
        setShowInputWalletName(value);
      }, 300);
    } else {
      setShowInputWalletName(value);
    }
  };

  const [showCustomSlippage, setShowCustomSlippage] = useState(false);
  const showCustomSlippageRef = useRef<boolean>(false);
  useEffect(() => {
    showCustomSlippageRef.current = showCustomSlippage;
  }, [showCustomSlippage]);

  const [
    showCreateWalletAndLoginDialog,
    setShowCreateWalletAndLoginDialog,
  ] = useState(false);
  const showCreateWalletAndLoginDialogRef = useRef(false);
  useEffect(() => {
    showCreateWalletAndLoginDialogRef.current =
      showCreateWalletAndLoginDialog;
  }, [showCreateWalletAndLoginDialog]);

  const [showTwofaDialog, setShowTwofaDialog] = useState(false);
  const showTwofaDialogRef = useRef(false);
  useEffect(() => {
    showTwofaDialogRef.current = showTwofaDialog;
  }, [showTwofaDialog]);

  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const showLoginDialogRef = useRef(false);
  useEffect(() => {
    showLoginDialogRef.current = showLoginDialog;
  }, [showLoginDialog]);

  const [showApproveTokenDialog, setShowApproveTokenDialog] =
    useState(false);
  const showApproveTokenDialogRef = useRef(false);
  useEffect(() => {
    showApproveTokenDialogRef.current = showApproveTokenDialog;
  }, [showApproveTokenDialog]);

  const [showInputInModal, setShowInputInModal] = useState(false);
  const showInputInModalRef = useRef<boolean>(false);
  useEffect(() => {
    showInputInModalRef.current = showInputInModal;
  }, [showInputInModal]);
  const _setShowInputInModal = (value: boolean) => {
    if (!value) {
      mainButton?.hide();
      setTimeout(() => {
        setShowInputInModal(value);
      }, 300);
    } else {
      setShowInputInModal(value);
    }
  };

  const [directToPage, setDirectToPage] = useState(false);
  return {
    homeScrollRef,
    showBalance,
    setShowBalance,
    isChangingWallet,
    setIsChangingWallet,
    recentWallets,
    setRecentWallets,
    currentWalletAddress,
    setCurrentWalletAddress,
    showInputWalletName,
    setShowInputWalletName: _setShowInputWalletName,
    showInputWalletNameRef,
    showCustomSlippage,
    setShowCustomSlippage,
    showCustomSlippageRef,
    showCreateWalletAndLoginDialog,
    setShowCreateWalletAndLoginDialog,
    showCreateWalletAndLoginDialogRef,
    showTwofaDialog,
    setShowTwofaDialog,
    showTwofaDialogRef,
    showLoginDialog,
    setShowLoginDialog,
    showLoginDialogRef,
    showApproveTokenDialog,
    setShowApproveTokenDialog,
    showApproveTokenDialogRef,
    setShowInputInModal: _setShowInputInModal,
    showInputInModal,
    showInputInModalRef,
    directToPage,
    setDirectToPage,
  };
});
